import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../Config";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// "2026-02-28" (or "2026-02-28T00:00:00.000Z") -> "28 February 2026"
const formatDateLong = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).split("T")[0].split("-");
  if (!year || !month || !day) return value;
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1]} ${year}`;
};

// Formats "YYYY-MM-DD" (or "YYYY-MM-DDTHH:mm:ss.sssZ") as "DD/MM/YYYY".
// Deliberately string-based rather than `new Date(...)` + local getters,
// so it can't be shifted by a day depending on the browser's timezone.
const formatDate = (value) => {
  if (!value) return "-";
  const datePart = String(value).split("T")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

// Shows money as ₹5,084.74 (display only; non-numeric values are left as is)
const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return `₹${value}`;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// invoiceid: the invoice to load and display (falls back to the :invoiceid route
// param so this works whether it's passed as a prop or rendered straight from a route)
const InvoiceView = ({ invoiceid: invoiceidProp }) => {
  const navigate = useNavigate();
  const { invoiceid: invoiceidParam } = useParams();
  const invoiceid = invoiceidProp || invoiceidParam;

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    if (!data) return;

    // Slashes -> dashes, drop other invalid filename characters, spaces -> underscores
    const clean = (s) =>
      String(s ?? "")
        .trim()
        .replace(/[\\/]+/g, "-")
        .replace(/[:*?"<>|]+/g, "")
        .replace(/\s+/g, "_");

    const previousTitle = document.title;
    document.title = ["invoice", clean(data.invoiceno), clean(data.guestname)]
      .filter(Boolean)
      .join("_");

    // Restore the original tab title when leaving this page
    return () => {
      document.title = previousTitle;
    };
  }, [data]);
  useEffect(() => {
    if (!invoiceid) {
      setIsLoading(false);
      return;
    }

    const fetchInvoice = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const res = await fetch(`${API_URL}/billing/invoice/${invoiceid}`);
        const result = await res.json();

        if (!res.ok || !result.success) {
          setLoadError(result.message || "Failed to load invoice.");
          return;
        }

        const inv = result.invoice;
        setData({
          invoiceno: inv.invoiceno,
          invoicedate: inv.invoicedate,
          guestname: inv.guestname,
          gstno: inv.gstno,
          country: inv.country,
          rows: inv.items.map((item) => ({
            particulars: item.particulars,
            from: item.checkin,
            to: item.checkout,
            nights: item.nights,
            rate: item.rate,
            cgst: item.cgst,
            sgst: item.sgst,
          })),
          inWords: inv.amountinwords,
          subTotal: inv.subtotal,
          gst: inv.gst,
          total: inv.total,
        });
      } catch (err) {
        console.error("Error loading invoice:", err);
        setLoadError("Something went wrong while loading the invoice.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceid]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-10 text-center text-gray-600">
        Loading invoice...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto p-10 text-center text-red-600">
        {loadError}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-4xl mx-auto p-10 text-center text-gray-600">
        No invoice to display.
      </div>
    );
  }

  const {
    invoiceno,
    invoicedate,
    guestname,
    gstno,
    country,
    rows,
    inWords,
    subTotal,
    gst,
    total,
  } = data;

  // Show the GST design only when the saved invoice actually has GST
  const includeGst = Number(gst) > 0;

  const label = "text-[10px] tracking-wide text-[#7b807a] uppercase";

  return (
    <div className="cb-invoice max-w-4xl mx-auto px-12 py-12 bg-white text-[#1c2420] print:p-0">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
        .cb-invoice { font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif; }
        .cb-title { font-weight: 500; letter-spacing: -0.04em; line-height: 0.88; }
        @media print {
          @page { margin: 0; }
          body { margin: 1.6cm; }
          .no-print { display: none; }
        }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-start">
        <h1 className="cb-title text-[76px] uppercase">Invoice</h1>
        <div className="w-28 h-28 shrink-0">
          <img
            src="https://zorbastays.com/android-chrome-512x512.png"
            alt="Zorba Stays Logo"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Invoice Meta */}
      <div className="flex gap-16 mt-10 pb-6 border-b border-[#1c2420]">
        <div>
          <p className={label}>Invoice No.</p>
          <p className="mt-1 text-sm font-bold">{invoiceno}</p>
        </div>
        <div>
          <p className={label}>Date</p>
          <p className="mt-1 text-sm font-bold">
            {formatDateLong(invoicedate)}
          </p>
        </div>
      </div>

      {/* Bill To / From */}
      <div className="flex justify-between items-start py-6 border-b border-[#1c2420]">
        <div className="w-1/2 text-xs tracking-wider leading-[1.9] text-[#3b413a]">
          <p className={label}>Bill To</p>
          <p className="mt-3 text-base font-bold text-[#1c2420] leading-normal">
            {guestname}
          </p>
          {includeGst && gstno && <p>GSTIN NO.: {gstno}</p>}
          {country && <p>{country}</p>}
        </div>
        <div className="text-right text-xs tracking-wider leading-[1.9] text-[#3b413a]">
          <p className={label}>From</p>
          <p className="mt-3 text-base font-bold text-[#1c2420] leading-normal">
            Zorba Stays
          </p>
          {includeGst && <p>GSTIN NO.: 24AADFZ2976K1ZO</p>}
          <p>306-7 - Business Centrum,</p>
          <p>Amin Marg,</p>
          <p>Rajkot, 360001</p>
          <p>India</p>
        </div>
      </div>

      {/* Table (Read Only) */}
      <table className="w-full border-collapse text-xs mt-8">
        <thead>
          <tr className="text-[10px] uppercase text-[#7b807a] text-left align-bottom">
            <th className="pb-4 font-normal w-[50px]">
              Sr.
              {/* <br /> */}
              No.
            </th>
            <th
              className={`pb-4 font-normal ${includeGst ? "w-[24%]" : "w-[34%]"}`}
            >
              Particulars
            </th>
            <th
              className={`pb-4 font-normal ${includeGst ? "w-[28%]" : "w-[26%]"}`}
            >
              Period of stay
            </th>
            <th className="pb-4 font-normal text-center">No. of nights</th>
            <th className="pb-4 font-normal text-right">Per night rate</th>
            {includeGst && (
              <>
                <th className="pb-4 font-normal text-right">CGST</th>
                <th className="pb-4 font-normal text-right">SGST</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="border-t border-[#1c2420]">
          {rows?.map((row, index) => (
            <tr key={index} className="align-top leading-relaxed">
              <td className="py-4">{index + 1}</td>
              <td className="py-4 pr-4 whitespace-pre-line break-words">
                {row.particulars}
              </td>
              <td className="py-4 pr-2">
                {formatDate(row.from)} - {formatDate(row.to)}
              </td>
              <td className="py-4 text-center">{row.nights}</td>
              <td className="py-4 text-right">{formatMoney(row.rate)}</td>
              {includeGst && (
                <>
                  <td className="py-4 text-right">{formatMoney(row.cgst)}</td>
                  <td className="py-4 text-right">{formatMoney(row.sgst)}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="border-t border-[#1c2420] text-xs">
        {includeGst && (
          <>
            <div className="flex justify-end items-center py-3">
              <span className="mr-24">Sub Total</span>
              <span className="w-28 text-right">{formatMoney(subTotal)}</span>
            </div>
            <div className="flex justify-end items-center pb-3">
              <span className="mr-24">GST(18%)</span>
              <span className="w-28 text-right">{formatMoney(gst)}</span>
            </div>
          </>
        )}
        <div
          className={`flex justify-end items-center py-4 font-bold border-b border-[#1c2420] ${
            includeGst ? "border-t" : ""
          }`}
        >
          <span className="mr-24">Total</span>
          <span className="w-28 text-right">{formatMoney(total)}</span>
        </div>
      </div>

      {/* In Words */}
      <div className="mt-8">
        <p className={label}>In Words</p>
        <p className="mt-1 text-sm font-bold">{inWords}</p>
      </div>

      {/* Thank you */}
      <div className="mt-6 text-sm font-bold leading-snug">
        <p>Thank you!</p>
        <p>It’s been a pleasure hosting you!</p>
      </div>

      {/* Footer */}
      <div className="mt-14 flex items-center gap-3">
        <div className="flex-1 border-t border-[#1c2420]"></div>
        <img
          src="https://dash.zorbastays.com/assets/zorba_logo.webp"
          alt="Zorba Stays"
          className="w-6 h-6 object-contain"
        />
        <div className="flex-1 border-t border-[#1c2420]"></div>
      </div>
      <div className="mt-4 flex justify-between text-[10px] text-[#3b413a]">
        <span>@zorba_stays</span>
        <span>www.zorbastays.com</span>
      </div>

      {/* Print Button */}
      <div className="flex justify-center gap-4 mt-10 mb-4 no-print">
        <button
          onClick={() => window.print()}
          className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
        >
          Print Invoice
        </button>
        <button
          onClick={() => navigate("/bills")}
          className="px-6 py-2 rounded-lg border border-gray-400 text-gray-700 hover:bg-gray-100 transition-all duration-300 text-sm"
        >
          Back to Bills
        </button>
      </div>
    </div>
  );
};

export default InvoiceView;

// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import { useNavigate } from "react-router-dom";
// import { API_URL } from "../../Config";

// // Formats "YYYY-MM-DD" (or "YYYY-MM-DDTHH:mm:ss.sssZ") as "DD-MM-YYYY".
// // Deliberately string-based rather than `new Date(...)` + local getters,
// // so it can't be shifted by a day depending on the browser's timezone.
// const formatDate = (value) => {
//   if (!value) return "-";
//   const datePart = String(value).split("T")[0];
//   const [year, month, day] = datePart.split("-");
//   if (!year || !month || !day) return value;
//   return `${day}/${month}/${year}`;
// };
// // invoiceid: the invoice to load and display (falls back to the :invoiceid route
// // param so this works whether it's passed as a prop or rendered straight from a route)
// const InvoiceView = ({ invoiceid: invoiceidProp }) => {
//   const navigate = useNavigate();
//   const { invoiceid: invoiceidParam } = useParams();
//   const invoiceid = invoiceidProp || invoiceidParam;

//   const [data, setData] = useState(null);
//   const [isLoading, setIsLoading] = useState(true);
//   const [loadError, setLoadError] = useState(null);

//   useEffect(() => {
//     if (!invoiceid) {
//       setIsLoading(false);
//       return;
//     }

//     const fetchInvoice = async () => {
//       try {
//         setIsLoading(true);
//         setLoadError(null);
//         const res = await fetch(`${API_URL}/billing/invoice/${invoiceid}`);
//         const result = await res.json();

//         if (!res.ok || !result.success) {
//           setLoadError(result.message || "Failed to load invoice.");
//           return;
//         }

//         const inv = result.invoice;
//         setData({
//           invoiceno: inv.invoiceno,
//           invoicedate: inv.invoicedate,
//           guestname: inv.guestname,
//           rows: inv.items.map((item) => ({
//             particulars: item.particulars,
//             from: item.checkin,
//             to: item.checkout,
//             nights: item.nights,
//             rate: item.rate,
//             cgst: item.cgst,
//             sgst: item.sgst,
//           })),
//           inWords: inv.amountinwords,
//           subTotal: inv.subtotal,
//           gst: inv.gst,
//           total: inv.total,
//         });
//       } catch (err) {
//         console.error("Error loading invoice:", err);
//         setLoadError("Something went wrong while loading the invoice.");
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchInvoice();
//   }, [invoiceid]);

//   if (isLoading) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
//         Loading invoice...
//       </div>
//     );
//   }

//   if (loadError) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-red-600">
//         {loadError}
//       </div>
//     );
//   }

//   if (!data) {
//     return (
//       <div className="max-w-5xl mx-auto p-10 text-center text-gray-600">
//         No invoice to display.
//       </div>
//     );
//   }

//   const {
//     invoiceno,
//     invoicedate,
//     guestname,
//     rows,
//     inWords,
//     subTotal,
//     gst,
//     total,
//   } = data;

//   return (
//     <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
//       <style>{`
//         @media print { @page { margin: 0; } body { margin: 1.6cm; } .no-print { display: none; } }
//       `}</style>

//       {/* Header */}
//       <div className="flex justify-between items-start mb-6">
//         <div>
//           <h1 className="text-4xl font-bold mb-2">Invoice</h1>
//           <br />
//           <br />
//           <h1 className="nt-seawave text-4xl mb-2 text-black">ZORBA STAYS</h1>
//           <p className="text-sm">
//             306, 7 - Business Centrum <br /> Amin Marg, Rajkot, 360001
//           </p>
//           {/* <p className="text-sm">GSTIN No: 24AADFZ2976K1ZO</p> */}
//         </div>
//         <div className="w-24 h-24">
//           <img
//             src="https://zorbastays.com/android-chrome-512x512.png"
//             alt="Zorba Stays Logo"
//             className="w-full h-full object-contain"
//           />
//         </div>
//       </div>

//       <div className="border-t border-black mb-6"></div>

//       {/* Invoice Meta */}
//       <div className="flex gap-16 mb-8">
//         <div>
//           <p className="text-xs font-bold uppercase">Invoice No.</p>
//           <p className="font-semibold">{invoiceno}</p>
//         </div>
//         <div>
//           <p className="text-xs font-bold uppercase">Invoice Date</p>
//           <p className="font-semibold">{formatDate(invoicedate)}</p>
//         </div>
//       </div>

//       <div className="mb-8">
//         <p className="font-bold">Invoice To:-</p>
//         <p className="text-lg">{guestname}</p>
//       </div>

//       {/* Table (Read Only) */}
//       <table className="w-full border-collapse border-t border-b border-black text-center text-sm">
//         <thead>
//           <tr className="uppercase text-xs font-bold border-b border-black">
//             <th className="py-3 border-black w-[60px]">Sr. No.</th>
//             <th className="py-3">Particulars</th>
//             <th className="py-3">Period of stay</th>
//             <th className="py-3">No. Nights</th>
//             <th className="py-3">Per Night Rate</th>
//             <th className="py-3">CGST</th>
//             <th className="py-3">SGST</th>
//           </tr>
//         </thead>
//         <tbody>
//           {rows?.map((row, index) => (
//             <tr key={index} className="border-b border-black">
//               <td className="py-3  border-black">{index + 1}</td>
//               <td className="py-3">{row.particulars}</td>
//               <td className="py-3">
//                 {formatDate(row.from)} to {formatDate(row.to)}
//               </td>
//               <td className="py-3">{row.nights}</td>
//               <td className="py-3">{row.rate}</td>
//               <td className="py-3">{row.cgst}</td>
//               <td className="py-3">{row.sgst}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* Totals */}
//       <div className="flex justify-between items-start mt-6 px-2">
//         <div className="w-1/2">
//           <p className="font-bold mb-1">In Words:</p>
//           <p className="uppercase border-b border-black">{inWords}</p>
//         </div>
//         <div className="w-64">
//           <div className="flex justify-between py-1">
//             <span className="font-bold">Sub Total</span>
//             <span>{subTotal}</span>
//           </div>
//           <div className="flex justify-between py-1 border-t border-black">
//             <span className="font-bold">GST</span>
//             <span>{gst}</span>
//           </div>
//           <div className="flex justify-between py-2 font-bold border-t border-black mt-1">
//             <span>Total</span>
//             <span>{total}</span>
//           </div>
//           <div className="flex justify-between py-1 border-t border-black"></div>
//         </div>
//       </div>

//       <div className="mt-20 text-center text-xs border-t border-black pt-8">
//         <p>www.zorbastays.com | Contact: +91 63552 31066, +91 98750 28066</p>
//       </div>

//       {/* Print Button */}
//       <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
//         <button
//           onClick={() => window.print()}
//           className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//         >
//           Print Invoice
//         </button>
//         <button
//           onClick={() => navigate("/bills")}
//           className="px-6 py-2 rounded-lg border border-gray-400 text-gray-700 hover:bg-gray-100 transition-all duration-300 text-sm"
//         >
//           Back to Bills
//         </button>
//       </div>
//     </div>
//   );
// };

// export default InvoiceView;
